// Runtime UI auto-translator.
//
// Strategy: walk every text node in the document. For each unique English
// string, check localStorage cache. Uncached strings get batched and sent
// to /.netlify/functions/translate which uses Claude Haiku and caches via
// Supabase so multi-user apps warm the cache together.
//
// A MutationObserver watches for DOM changes so React re-renders + new
// pages picked up. Once a string is translated, replacements are instant
// (localStorage hit, no round trip).
//
// Only activates when target lang !== 'en'. In English mode we're a no-op.

const CACHE_PREFIX = "scenta-i18n-cache:";
const BATCH_DELAY_MS = 250;
const MAX_LEN = 800; // don't translate long paragraphs — too slow / noisy

// Attribute names that render user-visible text and should also be translated.
const ATTR_TARGETS = ["placeholder", "title", "aria-label", "alt"] as const;

let currentTarget: string = "en";
let inFlight = new Set<string>();
let pendingQueue = new Set<string>();
let batchTimer: ReturnType<typeof setTimeout> | null = null;
let observer: MutationObserver | null = null;
const memoryCache = new Map<string, string>(); // src → translated
const originalTextByNode = new WeakMap<Text, string>(); // preserve the ENGLISH original
const originalAttrByEl = new WeakMap<Element, Map<string, string>>();

function cacheKey(lang: string, src: string): string {
  return CACHE_PREFIX + lang + ":" + src;
}
function loadCached(src: string): string | null {
  if (memoryCache.has(src)) return memoryCache.get(src)!;
  try {
    const stored = localStorage.getItem(cacheKey(currentTarget, src));
    if (stored) {
      memoryCache.set(src, stored);
      return stored;
    }
  } catch { /* ignore */ }
  return null;
}
function storeCached(src: string, translated: string) {
  memoryCache.set(src, translated);
  try { localStorage.setItem(cacheKey(currentTarget, src), translated); } catch { /* quota */ }
}

// Heuristics for strings we should NOT translate.
function shouldSkip(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length === 0 || trimmed.length > MAX_LEN) return true;
  // Purely non-alphabetic (numbers, symbols, emojis): skip.
  if (!/[a-zA-Z]/.test(trimmed)) return true;
  // URLs / emails
  if (/^https?:\/\//i.test(trimmed)) return true;
  if (/^[\w.+-]+@[\w.-]+\.\w+$/.test(trimmed)) return true;
  // Looks like a code / SKU (all caps letters with numbers/dashes, no spaces)
  if (/^[A-Z0-9._-]+$/.test(trimmed) && trimmed.length < 32) return true;
  // Currency-only tokens like "$12.34" or "$1,234"
  if (/^\$?[\d,.]+%?$/.test(trimmed)) return true;
  // Single letter
  if (trimmed.length === 1) return true;
  return false;
}

function collectFromNode(node: Node, textAcc: Text[], attrAcc: { el: Element; attr: string; value: string }[]) {
  if (node.nodeType === Node.TEXT_NODE) {
    const t = node as Text;
    const src = originalTextByNode.get(t) ?? t.data;
    if (!shouldSkip(src)) textAcc.push(t);
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as Element;
  // Skip <script>, <style>, <code>, <pre>, and anything marked no-translate
  const tag = el.tagName.toLowerCase();
  if (tag === "script" || tag === "style" || tag === "noscript") return;
  if (el.hasAttribute("data-no-translate")) return;
  if (el.getAttribute("translate") === "no") return;
  // Attributes
  const origMap = originalAttrByEl.get(el);
  for (const attr of ATTR_TARGETS) {
    const currentVal = el.getAttribute(attr);
    if (currentVal == null) continue;
    const origVal = origMap?.get(attr) ?? currentVal;
    if (!shouldSkip(origVal)) attrAcc.push({ el, attr, value: origVal });
  }
  // Recurse
  for (let child = el.firstChild; child; child = child.nextSibling) {
    collectFromNode(child, textAcc, attrAcc);
  }
}

function applyIfCached(root: Node) {
  const texts: Text[] = [];
  const attrs: { el: Element; attr: string; value: string }[] = [];
  collectFromNode(root, texts, attrs);
  const needed = new Set<string>();
  for (const t of texts) {
    const src = originalTextByNode.get(t) ?? t.data;
    if (!originalTextByNode.has(t)) originalTextByNode.set(t, src);
    const hit = loadCached(src);
    if (hit != null) {
      if (t.data !== hit) t.data = hit;
    } else {
      needed.add(src);
    }
  }
  for (const { el, attr, value } of attrs) {
    let orig = originalAttrByEl.get(el);
    if (!orig) { orig = new Map(); originalAttrByEl.set(el, orig); }
    if (!orig.has(attr)) orig.set(attr, value);
    const src = orig.get(attr)!;
    const hit = loadCached(src);
    if (hit != null) {
      if (el.getAttribute(attr) !== hit) el.setAttribute(attr, hit);
    } else {
      needed.add(src);
    }
  }
  for (const s of needed) if (!inFlight.has(s)) pendingQueue.add(s);
  if (pendingQueue.size > 0) scheduleBatch();
}

function scheduleBatch() {
  if (batchTimer) return;
  batchTimer = setTimeout(async () => {
    batchTimer = null;
    if (pendingQueue.size === 0) return;
    const batch = Array.from(pendingQueue);
    pendingQueue.clear();
    for (const s of batch) inFlight.add(s);
    try {
      const res = await fetch("/.netlify/functions/translate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target: currentTarget, strings: batch }),
      });
      if (!res.ok) throw new Error(`translate ${res.status}`);
      const data = await res.json() as { translations: string[] };
      for (let i = 0; i < batch.length; i++) {
        const src = batch[i];
        const tr = data.translations[i];
        if (typeof tr === "string" && tr.length > 0) storeCached(src, tr);
      }
      // After we've stored the newly-translated strings, re-apply so DOM
      // picks them up without waiting for another mutation.
      applyIfCached(document.body);
    } catch (e) {
      console.warn("translate batch failed", e);
    } finally {
      for (const s of batch) inFlight.delete(s);
    }
  }, BATCH_DELAY_MS);
}

let sweepTimer: ReturnType<typeof setInterval> | null = null;

export function startAutoTranslate(target: string) {
  currentTarget = target;
  if (target === "en") {
    // Reverse everything back to English by restoring the originals we saved.
    document.querySelectorAll("*").forEach(el => {
      const orig = originalAttrByEl.get(el);
      if (orig) for (const [attr, val] of orig) el.setAttribute(attr, val);
    });
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    let n: Node | null;
    while ((n = walker.nextNode())) {
      const t = n as Text;
      const orig = originalTextByNode.get(t);
      if (orig != null && t.data !== orig) t.data = orig;
    }
    if (observer) { observer.disconnect(); observer = null; }
    if (sweepTimer) { clearInterval(sweepTimer); sweepTimer = null; }
    return;
  }

  // React may mount / re-render nodes between now and the observer arming.
  // Kick a full-body pass now, another after animation frame, and a third
  // after a short delay so the initial sidebar + any lazy-loaded route both
  // get caught even if their mutations fire before we're listening.
  const kick = () => applyIfCached(document.body);
  kick();
  requestAnimationFrame(kick);
  setTimeout(kick, 250);
  setTimeout(kick, 1000);

  if (observer) observer.disconnect();
  observer = new MutationObserver(muts => {
    for (const m of muts) {
      if (m.type === "characterData" && m.target.nodeType === Node.TEXT_NODE) {
        applyIfCached(m.target);
      } else if (m.type === "childList") {
        m.addedNodes.forEach(n => applyIfCached(n));
      } else if (m.type === "attributes" && m.target.nodeType === Node.ELEMENT_NODE) {
        applyIfCached(m.target);
      }
    }
  });
  observer.observe(document.body, {
    characterData: true,
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: [...ATTR_TARGETS],
  });

  // Belt-and-suspenders: React reconciler can replace text nodes without
  // the observer catching every change quickly enough (component memoization,
  // portal edge cases). A cheap periodic sweep re-applies cached translations
  // to anything the observer missed. Purely cache-driven — no LLM calls.
  if (sweepTimer) clearInterval(sweepTimer);
  sweepTimer = setInterval(() => {
    if (currentTarget === "en") return;
    applyIfCached(document.body);
  }, 2000);
}

export function stopAutoTranslate() {
  if (observer) { observer.disconnect(); observer = null; }
  if (sweepTimer) { clearInterval(sweepTimer); sweepTimer = null; }
}
